import { memo, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { motion, useReducedMotion } from "motion/react";
import { AlertTriangle, Check, Copy, RefreshCw, RotateCw, Sparkles } from "lucide-react";
import { cn } from "../../ui/utils";
import { ChatMarkdown } from "./ChatMarkdown";
import { SuggestionChip } from "./SuggestionCard";
import type { ChatMessage } from "../types";
import { EASE_OUT, enter } from "../lib/motion";
import {
  ENABLE_TYPING_PREVIEW,
  joinTokens,
  segmentMarkdown,
  useSegmentReveal,
  useStreamingReveal,
  type MarkdownSegment,
  type RevealPos,
} from "../lib/typingPreview";

function formatTime(ts: number): string {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function renderInline(text: string): ReactNode[] {
  return text.split("**").map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold">
        {part}
      </strong>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

/** Stable empty segments, so the reveal effect does not re-run on every render. */
const NO_SEGMENTS: MarkdownSegment[] = [];

function ActionButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        "flex size-7 items-center justify-center rounded-lg text-muted-foreground transition-colors",
        "hover:bg-accent hover:text-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
      )}
    >
      {children}
    </button>
  );
}

interface MessageBubbleProps {
  message: ChatMessage;
  onSend: (prompt: string) => void;
  onRetry: (message: ChatMessage) => void;
  isLast?: boolean;
}

// memo(): REQUIRES stable onSend/onRetry from ChatPanel or every bubble re-renders.
export const MessageBubble = memo(function MessageBubble({
  message,
  onSend,
  onRetry,
  isLast = false,
}: MessageBubbleProps) {
  const isUser = message.role === "user";

  if (isUser) {
    return (
      <motion.div {...enter} className="ca-msg flex flex-col items-end">
        <div
          className={cn(
            "relative max-w-[85%] rounded-2xl rounded-tr-md px-3.5 py-2.5 text-white",
            "bg-gradient-to-br from-indigo-600 to-violet-600 shadow-[0_4px_14px_-6px_rgba(99,102,241,0.55)]",
            // No backdrop-blur here: it repainted every scroll frame; gradient opacity was raised instead.
            "dark:from-indigo-500/55 dark:to-violet-500/50 dark:text-white/95 dark:shadow-[0_8px_24px_-14px_rgba(124,58,237,0.5)] dark:ring-1 dark:ring-inset dark:ring-white/10",
          )}
        >
          <p className="whitespace-pre-wrap text-[0.8125rem] leading-relaxed">
            {message.text ? renderInline(message.text) : ""}
          </p>
        </div>
        <span className="mt-1 px-1 text-[0.6875rem] font-medium tabular-nums text-muted-foreground">
          {formatTime(message.createdAt)}
        </span>
      </motion.div>
    );
  }

  if (message.error) {
    return (
      <motion.div {...enter}>
        <div className="w-full min-w-0">
          <div className="flex items-start gap-2.5 rounded-xl border border-red-200/70 bg-red-50/70 px-3.5 py-2.5 dark:border-red-500/25 dark:bg-red-500/[0.08]">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-500" />
            <p className="text-[0.8125rem] leading-relaxed text-foreground/80">
              {message.text ?? "Something went wrong while generating a response."}
            </p>
          </div>
          {message.sourcePrompt && (
            <button
              type="button"
              onClick={() => onRetry(message)}
              className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card/60 px-3 py-1.5 text-[0.75rem] font-medium text-foreground shadow-sm transition-all hover:-translate-y-px hover:border-primary/40 hover:bg-primary/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background dark:border-white/10 dark:bg-white/[0.05]"
            >
              <RefreshCw className="size-3.5" />
              Try again
            </button>
          )}
        </div>
      </motion.div>
    );
  }

  return <AssistantBubble message={message} onSend={onSend} onRetry={onRetry} isLast={isLast} />;
});

const played = new Set<string>();

function RevealedMarkdown({
  segments,
  pos,
  done,
}: {
  segments: MarkdownSegment[];
  pos: RevealPos;
  done: boolean;
}) {
  return (
    <div className="space-y-3">
      {segments.map((seg, i) => {
        if (i > pos.seg) return null;
        if (seg.kind === "block") {
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18, ease: EASE_OUT }}
            >
              <ChatMarkdown content={seg.text} />
            </motion.div>
          );
        }
        const isCurrent = i === pos.seg && !done;
        const text = isCurrent ? joinTokens(seg.tokens, pos.tok) : seg.text;
        return (
          <div key={i} className={isCurrent ? "ca-caret" : undefined}>
            <ChatMarkdown content={text} />
          </div>
        );
      })}
    </div>
  );
}

function AssistantBubble({ message, onSend, onRetry, isLast = false }: MessageBubbleProps) {
  const reduceMotion = useReducedMotion();
  const [copied, setCopied] = useState(false);

  const alreadyPlayedRef = useRef(played.has(message.id));

  const animateEnabled =
    ENABLE_TYPING_PREVIEW &&
    !!message.animate &&
    isLast &&
    !reduceMotion &&
    !alreadyPlayedRef.current;

  // Paces bursty server chunks into a steady type-out while tokens are still arriving.
  const { visible: streamedText, settled: streamSettled } = useStreamingReveal(
    message.text ?? "",
    !!message.streaming && !reduceMotion,
  );

  // Only the typewriter path reads segments, so skip re-parsing the whole answer on every
  // delta while tokens stream in — that ran segmentMarkdown once per token.
  const segments = useMemo(
    () => (animateEnabled ? segmentMarkdown(message.text ?? "") : NO_SEGMENTS),
    [animateEnabled, message.text],
  );
  const { pos, done, skip } = useSegmentReveal(segments, animateEnabled);

  useEffect(() => {
    if (animateEnabled && done) played.add(message.id);
  }, [animateEnabled, done, message.id]);

  const revealing = animateEnabled && !done;
  // While tokens are still streaming in, suppress the copy/regenerate/suggested
  // extras — the turn isn't finished yet.
  // streamSettled as well as !streaming: the terminal answer arrives before the paced reveal
  // has finished typing it, and the actions must not appear over a half-written answer.
  const showExtras = (!animateEnabled || done) && !message.streaming && streamSettled;

  const copy = () => {
    if (!message.text) return;
    void navigator.clipboard?.writeText(message.text).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  };

  const canRegenerate = isLast && !!message.sourcePrompt;
  const hasFollowUps = !!message.followUps && message.followUps.length > 0;

  // A finished assistant turn with no visible text and no follow-ups would render as an empty
  // container (an orphan bubble + action row). Drop it entirely. Streaming bubbles are exempt so
  // the live caret still shows before the first token — they always carry at least one character.
  const hasText = !!message.text && message.text.trim().length > 0;
  if (!hasText && !hasFollowUps && !message.streaming) {
    return null;
  }

  return (
    <motion.div {...enter} className="ca-msg">
      <div className="w-full min-w-0">
        <div
          onClick={revealing ? skip : undefined}
          className={cn(
            "text-[0.8125rem] leading-relaxed text-foreground",
            revealing && "cursor-default",
            // Blinking caret while live tokens are still arriving.
            message.streaming && "ca-caret",
          )}
        >
          {message.text &&
            (animateEnabled ? (
              <RevealedMarkdown segments={segments} pos={pos} done={done} />
            ) : (
              <ChatMarkdown content={streamedText} />
            ))}
        </div>

        {showExtras && (
          <div className="-ml-1.5 mt-1.5 flex items-center gap-0.5">
            <ActionButton label={copied ? "Copied" : "Copy"} onClick={copy}>
              {copied ? <Check className="size-3.5 text-emerald-500" /> : <Copy className="size-3.5" />}
            </ActionButton>
            {canRegenerate && (
              <ActionButton label="Regenerate" onClick={() => onRetry(message)}>
                <RotateCw className="size-3.5" />
              </ActionButton>
            )}
            <span className="ml-1 text-[0.6875rem] font-medium tabular-nums text-muted-foreground">
              {formatTime(message.createdAt)}
            </span>
          </div>
        )}

        {showExtras && isLast && hasFollowUps && (
          <div className="mt-3.5">
            <p className="mb-2 flex items-center gap-1.5 text-[0.6875rem] font-semibold tracking-wider text-muted-foreground">
              <Sparkles className="size-3.5 text-primary" />
              Suggested
            </p>
            <div className="flex flex-wrap gap-1.5">
              {message.followUps!.map((f, i) => (
                <SuggestionChip key={i} title={f} onClick={() => onSend(f)} />
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
